/* W8.8 — Comparaison de décisions / allocation de capital.
 * DÉCOMPOSABLE : AdjEV, Capital Efficiency, Edge, Confidence, Downside, capital requis.
 * AUCUN score composite — l'allocation reste un arbitrage humain. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Scale, Loader2 } from "lucide-react";

const fmtCHF = (v) => v == null ? "—" : new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v);
const pct = (v) => v == null ? "—" : Math.round(v * 100) + "%";
const METRICS = [
  { key: "adjusted_ev_chf", label: "EV ajustée (AdjEV)", fmt: fmtCHF },
  { key: "ev_chf", label: "EV brute", fmt: fmtCHF },
  { key: "capital_efficiency", label: "Efficience du capital", fmt: (v) => v == null ? "—" : v },
  { key: "edge", label: "Edge vs marché", fmt: (v) => v == null ? "—" : (v >= 0 ? "+" : "") + Math.round(v * 100) + " pts" },
  { key: "confidence_avg", label: "Confiance", fmt: pct },
  { key: "p_success", label: "P(succès)", fmt: pct },
  { key: "downside_chf", label: "Perte max (downside)", fmt: fmtCHF },
  { key: "capital_at_risk_chf", label: "Capital requis", fmt: fmtCHF },
  { key: "transaction_costs_chf", label: "Coûts de transaction", fmt: fmtCHF },
  { key: "breakeven_p_success", label: "Seuil de rentabilité P(succès)", fmt: pct },
];

export function DecisionCompare() {
  const [decisions, setDecisions] = useState(null);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/workspace/decisions").then((r) => setDecisions(r.data.items)).catch((e) => notify.error(fmtError(e))); }, []);

  useEffect(() => {
    if (selected.length < 2) { setResult(null); return; }
    setBusy(true);
    api.get(`/workspace/decisions/compare?ids=${selected.join(",")}`)
      .then((r) => setResult(r.data)).catch((e) => notify.error(fmtError(e))).finally(() => setBusy(false));
  }, [selected]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(0, 10));
  if (!decisions) return <div className="flex justify-center py-16 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const best = {};
  if (result) Object.entries(result.rankings || {}).forEach(([m, order]) => { if (order.length) best[m] = order[0]; });

  return (
    <div className="space-y-3" data-testid="decision-compare">
      <div className="text-[10px] px-2.5 py-1.5 rounded border border-white/10 text-zinc-400 bg-white/[0.02] flex items-center gap-1.5">
        <Scale className="w-3.5 h-3.5 text-[#7F77DD]" /> Comparaison décomposable — aucun score composite. L'allocation de capital reste un arbitrage humain.
      </div>
      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-widest text-zinc-600">Sélectionnez 2 à 10 décisions</div>
        {decisions.length === 0 && <div className="p-6 text-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg">Aucune décision journalisée à comparer.</div>}
        <div className="grid gap-1 md:grid-cols-2">
          {decisions.map((d) => (
            <label key={d.id} className={`flex items-center gap-2 text-[11px] p-2 rounded border cursor-pointer transition-colors ${selected.includes(d.id) ? "border-[#7F77DD]/50 bg-[#7F77DD]/5 text-zinc-200" : "border-white/10 text-zinc-400 hover:bg-white/[0.03]"}`} data-testid={`compare-select-${d.id}`}>
              <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} className="accent-[#7F77DD]" />
              <span className="flex-1 truncate">{d.title}</span>
              <span className="text-[9px] text-zinc-600">{(d.context || {}).system || "platform"} · {d.status}</span>
            </label>
          ))}
        </div>
      </div>
      {busy && <div className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Comparaison…</div>}
      {result && (
        <div className="noam-card p-3 overflow-x-auto" data-testid="compare-table">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-zinc-500">
                <th className="py-1.5 pr-3 font-normal uppercase tracking-widest text-[9px]">Métrique</th>
                {result.items.map((it) => (
                  <th key={it.id} className="py-1.5 pr-3 font-medium text-zinc-200">
                    <div className="truncate max-w-[160px]">{it.title}</div>
                    <div className="text-[9px] font-normal text-zinc-500">{it.system} · {it.status}{it.verdict ? ` · ${it.verdict}` : ""} · v{it.estimation_version}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRICS.map((m) => (
                <tr key={m.key} className="border-t border-white/5">
                  <td className="py-1.5 pr-3 text-zinc-500">{m.label}</td>
                  {result.items.map((it) => (
                    <td key={it.id} className={`py-1.5 pr-3 ${best[m.key] === it.id ? "text-[#1D9E75] font-semibold" : "text-zinc-300"}`} data-testid={`compare-cell-${m.key}-${it.id}`}>
                      {m.fmt(it.metrics[m.key])}{best[m.key] === it.id && result.items.length > 1 ? " ●" : ""}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-white/5">
                <td className="py-1.5 pr-3 text-zinc-500">Alertes</td>
                {result.items.map((it) => (
                  <td key={it.id} className="py-1.5 pr-3 text-[9px] text-[#EF9F27]">{(it.flags || []).join(", ") || "—"}</td>
                ))}
              </tr>
            </tbody>
          </table>
          <div className="text-[9px] text-zinc-600 mt-2">● = meilleure valeur sur la métrique (downside le moins sévère, capital requis le plus faible).</div>
        </div>
      )}
    </div>
  );
}
